import { createFeatureSelector, createReducer, on } from '@ngrx/store';
import type { ParsedPushNotificationPayload } from '@hunting-nature/shared-core/interfaces/push-notification-payload.interface';

import { createScopedSelector } from '../../utils/factory.utils';

import { PushActions, type PushSkipReason } from './push.actions';

export const PUSH_STORE_KEY = 'push';

/**
 * The OS answer, and nothing else — see `PushState.skipReason` for why push may never have
 * started. Folding the two together would report a build with push switched off as a
 * device that cannot receive notifications at all.
 */
export type PushPermission = 'unknown' | 'granted' | 'denied';

export interface PushState {
  permission: PushPermission;
  /**
   * Why push never started, or null once an attempt got past the guards. Independent of
   * {@link PushPermission}: a skipped init leaves the OS permission untouched.
   */
  skipReason: PushSkipReason | null;
  /** Native handle, kept so a repeated login can skip a redundant backend round-trip. */
  token: string | null;
  /** Azure Notification Hub installation id — mirrored here so logout can read it synchronously. */
  installationId: string | null;
  /** True once the backend confirmed the hub installation for the current token. */
  isRegisteredWithBackend: boolean;
  /** Parsed notification tap payload kept until auth/bootstrap is ready to consume it. */
  pendingNotificationIntent: ParsedPushNotificationPayload | null;
  lastError: unknown;
}

export const initialPushState: PushState = {
  permission: 'unknown',
  skipReason: null,
  token: null,
  installationId: null,
  isRegisteredWithBackend: false,
  pendingNotificationIntent: null,
  lastError: null,
};

// selectors
const selectPushState = createFeatureSelector<PushState>(PUSH_STORE_KEY);
const createPushSelector = createScopedSelector(selectPushState);

// reducers
export const pushReducer = createReducer(
  initialPushState,

  on(
    PushActions.initPush,
    (state): PushState => ({
      ...state,
      // A new attempt: whatever stopped the previous one no longer holds until the effect
      // says so again.
      skipReason: null,
    })
  ),
  on(
    PushActions.initPushSkipped,
    (state, action): PushState => ({
      ...state,
      // Leaves `permission` alone on purpose — nothing was asked, so the stored answer is
      // still the truth.
      skipReason: action.reason,
    })
  ),
  on(
    PushActions.installationIdResolved,
    (state, action): PushState => ({
      ...state,
      installationId: action.installationId,
    })
  ),
  on(
    PushActions.permissionGranted,
    (state): PushState => ({
      ...state,
      permission: 'granted',
    })
  ),
  on(
    PushActions.permissionDenied,
    (state): PushState => ({
      ...state,
      permission: 'denied',
      // A denied permission invalidates any previous registration: the hub record may
      // still exist, but the OS will not deliver to it.
      isRegisteredWithBackend: false,
    })
  ),
  on(
    PushActions.tokenReceived,
    (state, action): PushState => ({
      ...state,
      token: action.token,
      // A rotated token is not registered until the backend says so.
      isRegisteredWithBackend: state.token === action.token && state.isRegisteredWithBackend,
    })
  ),
  on(
    PushActions.tokenFailed,
    (state, action): PushState => ({
      ...state,
      lastError: action.error,
    })
  ),
  on(
    PushActions.registerDeviceSucceeded,
    (state, action): PushState => ({
      ...state,
      token: action.token,
      isRegisteredWithBackend: true,
      lastError: null,
    })
  ),
  on(
    PushActions.registerDeviceFailed,
    (state, action): PushState => ({
      ...state,
      isRegisteredWithBackend: false,
      lastError: action.error,
    })
  ),
  on(
    // A failed delete counts too: dropOsRegistrationOnLogout$ invalidates the OS handle either
    // way, so the app is no longer reachable under this registration. Keeping the flag set
    // would make registerDevice$ skip the next login — on iOS, where APNs hands back the same
    // token for the install, the hub installation would keep the previous user's tags.
    PushActions.unregisterDeviceSucceeded,
    PushActions.unregisterDeviceFailed,
    (state): PushState => ({
      ...state,
      token: null,
      pendingNotificationIntent: null,
      isRegisteredWithBackend: false,
    })
  ),
  on(
    PushActions.notificationActionPerformed,
    (state, action): PushState => ({
      ...state,
      pendingNotificationIntent: action.action.actionId === 'tap' ? action.payload : state.pendingNotificationIntent,
    })
  ),
  on(
    PushActions.clearPendingNotificationIntent,
    (state): PushState => ({
      ...state,
      pendingNotificationIntent: null,
    })
  )
);

export const PushSelectors = {
  permission: createPushSelector((state: PushState) => state.permission),
  skipReason: createPushSelector((state: PushState) => state.skipReason),
  token: createPushSelector((state: PushState) => state.token),
  installationId: createPushSelector((state: PushState) => state.installationId),
  isRegisteredWithBackend: createPushSelector((state: PushState) => state.isRegisteredWithBackend),
  pendingNotificationIntent: createPushSelector((state: PushState) => state.pendingNotificationIntent),
  lastError: createPushSelector((state: PushState) => state.lastError),
};
